import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { TextField } from "../shared/FormField";
import { apiClient, getApiErrorMessage, getFieldErrors } from "../../lib/apiClient";
import { todayLocal } from "../../lib/utils";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;
const EMPTY_FORM = { date: "", comment: "", image: "" };

export function DailyMenuFormDialog({ open, onOpenChange, menu, onSaved }) {
  const isEdit = Boolean(menu);
  const [form, setForm] = useState(EMPTY_FORM);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(menu ? { date: menu.date || "", comment: menu.comment || "", image: menu.image || "" } : { ...EMPTY_FORM, date: todayLocal() });
    setPreviewUrl(menu?.image ? `${API_BASE}/daily-menu/images/${menu.image}` : "");
    setErrors({});
  }, [open, menu]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreviewUrl(URL.createObjectURL(file));
    setUploading(true);
    const body = new FormData();
    body.append("image", file);
    try {
      const { data } = await apiClient.post("/daily-menu/upload-image", body);
      setForm((f) => ({ ...f, image: data.path }));
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not upload the image."));
      setPreviewUrl(menu?.image ? `${API_BASE}/daily-menu/images/${menu.image}` : "");
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    setForm((f) => ({ ...f, image: "" }));
    setPreviewUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    const payload = { date: form.date || null, comment: form.comment || null, image: form.image || null };
    try {
      if (isEdit) {
        await apiClient.put(`/daily-menu/${menu.id}`, payload);
        toast.success("Daily menu entry updated");
      } else {
        await apiClient.post("/daily-menu", payload);
        toast.success("Daily menu entry created");
      }
      onSaved();
    } catch (error) {
      setErrors(getFieldErrors(error));
      toast.error(getApiErrorMessage(error, "Could not save this daily menu entry."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="daily-menu-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Daily Menu Entry" : "New Daily Menu Entry"}</DialogTitle>
          <DialogDescription>A photo + note shown to customers/drivers for a specific date.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <TextField
            label="Date" type="date" required value={form.date} onChange={setField("date")}
            error={errors.date} testId="daily-menu-form-date"
          />

          <div className="space-y-1.5">
            <Label>Menu Photo</Label>
            {previewUrl ? (
              <div className="relative w-full max-w-[220px]">
                <img
                  src={previewUrl} alt="Daily menu" data-testid="daily-menu-form-image-preview"
                  className="aspect-[4/3] w-full rounded-md border border-border object-cover"
                />
                <button
                  type="button" onClick={clearImage} data-testid="daily-menu-form-image-remove"
                  className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-danger text-white shadow"
                >
                  <X size={13} strokeWidth={2} />
                </button>
              </div>
            ) : (
              <label
                htmlFor="daily-menu-form-image-input"
                className="flex h-28 w-full max-w-[220px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border border-dashed border-border bg-surface text-text-muted hover:bg-background"
              >
                <ImagePlus size={20} strokeWidth={1.5} />
                <span className="text-xs">Upload photo</span>
              </label>
            )}
            <input
              ref={fileInputRef} id="daily-menu-form-image-input" type="file" accept="image/*"
              className="hidden" onChange={handleFileChange} data-testid="daily-menu-form-image-input"
            />
            {uploading && <p className="text-xs text-text-muted">Uploading...</p>}
            {errors.image && <p className="text-xs text-danger">{errors.image}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="daily-menu-form-comment">Comment / Note</Label>
            <Textarea
              id="daily-menu-form-comment" data-testid="daily-menu-form-comment" rows={3}
              value={form.comment} onChange={setField("comment")} placeholder="e.g. Today's special: Paneer Butter Masala"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} data-testid="daily-menu-form-cancel-button">Cancel</Button>
            <Button type="submit" variant="accent" disabled={saving || uploading} data-testid="daily-menu-form-submit-button">
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Entry"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
